import React, { useRef, useState } from 'react'; 
import { useNavigate } from 'react-router-dom';
import { DBTable } from '../components/DBTable';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';
import { useConfig } from '../hooks/useConfig';
import { usePermission } from '../hooks/usePermission';
import { AccessLevel } from '../types';
import JSZip from 'jszip';

const SysEdit: React.FC = () => {
  const { getTileStyles } = useConfig();
  const { hasPermission } = usePermission();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [currentAction, setCurrentAction] = useState<{ action: string, selectedIds: (string | number)[], state: 'download' | 'upload' } | null>(null);

  const hasAccess = hasPermission('app.sistema.sysedit', AccessLevel.ro);

  if (!hasAccess) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-8 text-center bg-white rounded-3xl border border-slate-100 shadow-xs max-w-2xl mx-auto my-12">
        <div id="no-access-icon-wrapper" className="w-16 h-16 bg-red-50 text-red-500 rounded-2xl flex items-center justify-center mb-4 border border-red-100/50">
          <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 15v2m0-6v4m0-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h1 className="text-xl font-black text-slate-800 uppercase tracking-tight">Accesso non consentito</h1>
        <p className="text-sm text-slate-500 mt-2 max-w-sm font-semibold">
          Non hai i permessi necessari per accedere a questa sezione. È richiesto l'inserimento nel gruppo <code className="bg-slate-50 border border-slate-100 px-1.5 py-0.5 rounded-lg font-mono text-xs text-red-600 font-bold">app.sistema.sysedit</code>.
        </p>
      </div>
    );
  }

  const downloadFolderPref = getTileStyles('Cartella download')?.folder_download;
  const uploadFolderPref = getTileStyles('Cartella upload')?.folder_upload;

  const downloadFile = async (content: Blob | string, fileName: string, action: string) => {
    const isBlob = content instanceof Blob;
    const extension = isBlob ? 'zip' : (action === 'csv' ? 'csv' : action === 'json' ? 'json' : 'sql');
    const contentType = isBlob ? 'application/zip' : (action === 'json' ? 'application/json' : action === 'csv' ? 'text/csv' : 'text/plain');

    // Try modern File System Access API first for explicit save location
    // Note: This often fails in iframes (cross-origin subframes)
    if ('showSaveFilePicker' in window) {
      try {
        const handle = await (window as any).showSaveFilePicker({
          suggestedName: fileName.endsWith(`.${extension}`) ? fileName : `${fileName}.${extension}`,
          types: [{
            description: isBlob ? 'ZIP Archive' : `${action.toUpperCase()} File`,
            accept: { [contentType]: [`.${extension}`] },
          }],
        });
        const writable = await handle.createWritable();
        await writable.write(content);
        await writable.close();
        return true;
      } catch (err: any) {
        // Silently catch security errors or aborts to allow fallback
        if (err.name === 'AbortError') return false;
        if (err.name === 'SecurityError' || err.message.includes('sub frames')) {
          console.warn('File System Access API blocked in this context. Using fallback download.');
        } else {
          console.error('File System API Error:', err);
        }
        // Fallback to traditional download if something fails or is blocked
      }
    }

    try {
      const blob = isBlob ? content : new Blob([content], { type: contentType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName.endsWith(`.${extension}`) ? fileName : `${fileName}.${extension}`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        if (a.parentNode) document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 100);
      return true;
    } catch (err) {
      console.error('Download error:', err);
      return false;
    }
  };

  const jsonToCsv = (items: any[]) => {
    if (!items || items.length === 0) return '';
    const headers = Object.keys(items[0]).join(';');
    const rows = items.map(item => 
      Object.values(item).map(val => {
        if (val === null) return '';
        if (typeof val === 'object') return `"${JSON.stringify(val).replace(/"/g, '""')}"`;
        return `"${String(val).replace(/"/g, '""')}"`;
      }).join(';')
    );
    return [headers, ...rows].join('\n');
  };

  const jsonToSql = (tableName: string, items: any[]) => {
    if (!items || items.length === 0) return '';
    const columns = Object.keys(items[0]).join(', ');
    const values = items.map(item => {
      const row = Object.values(item).map(val => {
        if (val === null) return 'NULL';
        if (typeof val === 'string') return `'${val.replace(/'/g, "''")}'`;
        if (typeof val === 'object') return `'${JSON.stringify(val).replace(/'/g, "''")}'`;
        return val;
      }).join(', ');
      return `(${row})`;
    }).join(',\n');
    return `INSERT INTO ${tableName} (${columns}) VALUES\n${values};`;
  };

  const handleAction = async (action: string, selectedIds: (string | number)[], state: 'download' | 'upload') => {
    if (state === 'download') {
      if (selectedIds.length === 0) return;
      
      const mode = 'Esportazione';
      const extension = action === 'csv' ? 'csv' : action === 'json' ? 'json' : 'sql';
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      
      let fileHandle: any = null;
      let zip: JSZip | null = null;

      // 1. Ask for location IMMEDIATELY to satisfy "User Activation" security
      // Note: In iframes, this may throw a SecurityError immediately
      
      if ('showSaveFilePicker' in window) {
        try {
          const suggestedName = selectedIds.length > 1 
            ? `gesvol_export_${timestamp}.zip` 
            : `${selectedIds[0]}.${extension}`;
          
          const contentType = selectedIds.length > 1 
            ? 'application/zip' 
            : (action === 'json' ? 'application/json' : action === 'csv' ? 'text/csv' : 'text/plain');

          fileHandle = await (window as any).showSaveFilePicker({
            suggestedName,
            types: [{
              description: selectedIds.length > 1 ? 'ZIP Archive' : `${action.toUpperCase()} File`,
              accept: { [contentType]: [`.${selectedIds.length > 1 ? 'zip' : extension}`] },
            }],
          });
        } catch (err: any) {
          if (err.name === 'AbortError') return;
          // SecurityError is expected in cross-origin iframes
          if (err.name !== 'SecurityError') {
            console.error('Save picker error:', err);
          }
          // fileHandle stays null, will use downloadFile fallback
        }
      }

      toast.info(`${mode} in corso...`);
      if (selectedIds.length > 1) zip = new JSZip();
      
      let successCount = 0;

      for (const tableId of selectedIds) {
        const toastId = `action-${tableId}`;
        toast.loading(`${mode} tabella: ${tableId}`, { id: toastId });
        
        try {
          const { data, error } = await supabase.from(String(tableId)).select('*');
          if (error) throw error;

          if (!data || data.length === 0) {
            toast.error(`Tabella ${tableId} vuota.`, { id: toastId });
            continue;
          }

          let content = '';
          if (action === 'json') {
            content = JSON.stringify(data, null, 2);
          } else if (action === 'csv') {
            content = jsonToCsv(data);
          } else if (action === 'sql') {
            content = jsonToSql(String(tableId), data);
          }

          if (zip) {
            zip.file(`${tableId}.${extension}`, content);
            toast.success(`Aggiunta: ${tableId}`, { id: toastId });
            successCount++;
          } else {
            // Single table export
            if (fileHandle) {
              const writable = await fileHandle.createWritable();
              await writable.write(content);
              await writable.close();
              toast.success(`${mode} completata: ${tableId}`, { id: toastId });
              successCount++;
            } else {
              // Fallback to standard download
              const success = await downloadFile(content, `${tableId}.${extension}`, action);
              if (success) {
                toast.success(`${mode} completata: ${tableId}`, { id: toastId });
                successCount++;
              }
            }
          }
        } catch (error: any) {
          console.error(error);
          const errorMsg = typeof error === 'object' ? (error.message || JSON.stringify(error)) : String(error);
          toast.error(`Errore su ${tableId}: ${errorMsg}`, { id: toastId });
        }
      }
      
      if (zip && successCount > 0) {
        toast.loading("Generazione file ZIP...", { id: 'zip-gen' });
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        
        if (fileHandle) {
          const writable = await fileHandle.createWritable();
          await writable.write(zipBlob);
          await writable.close();
          toast.success(`ZIP salvato con successo!`, { id: 'zip-gen' });
        } else {
          await downloadFile(zipBlob, `gesvol_export_${timestamp}.zip`, 'zip');
          toast.success(`ZIP generato!`, { id: 'zip-gen' });
        }
      }
    } else {
      // Import Mode
      setCurrentAction({ action, selectedIds, state });
      // Always use the standard file input for maximum compatibility/predictability
      fileInputRef.current?.click();
    }
  };

  const processFileImport = async (file: File, action: string, selectedIds: (string | number)[]) => {
    const reader = new FileReader();
    reader.onload = async (event) => {
      const content = event.target?.result as string;
      toast.info(`Importazione avviata per ${selectedIds.length} tabelle...`);

      try {
        let items: any[] = [];
        if (action === 'json') {
          items = JSON.parse(content);
        } else if (action === 'csv') {
          const lines = content.split('\n');
          const headers = lines[0].split(',');
          items = lines.slice(1).filter(l => l.trim()).map(line => {
            const values = line.split(',');
            return headers.reduce((obj: any, header, index) => {
              obj[header.trim()] = values[index]?.trim().replace(/^"|"$/g, '');
              return obj;
            }, {});
          });
        }

        for (const tableId of selectedIds) {
          toast.loading(`Importazione dati in ${tableId}...`, { id: `import-${tableId}` });
          const { error } = await supabase.from(String(tableId)).upsert(items);
          if (error) throw error;
          toast.success(`Tabella ${tableId} aggiornata!`, { id: `import-${tableId}` });
        }
        toast.success(`Importazione completata con successo!`);
      } catch (error: any) {
        console.error(error);
        const errorMsg = typeof error === 'object' ? (error.message || JSON.stringify(error)) : String(error);
        toast.error(`Errore durante l'importazione: ${errorMsg}`);
      }
    };
    reader.readAsText(file);
  };

  const handleFileImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !currentAction) return;
    await processFileImport(file, currentAction.action, currentAction.selectedIds);
    e.target.value = ''; // Reset input
  };

  const navigate = useNavigate();

  const handleRowDoubleClick = (row: any) => {
    navigate(`/sistema/sysedit/${row.table_name}`);
  };

  const [tables, setTables] = useState<any[]>([]);
  const [loadingTables, setLoadingTables] = useState(true);

  React.useEffect(() => {
    const fetchTables = async () => {
      try {
        // 1. Prova prima il proxy API (che ora usa la RPC internamente)
        const resp = await fetch('/api/sys/tables');
        if (resp.ok) {
          const data = await resp.json();
          if (data && data.length > 0) {
            setTables(data);
            setLoadingTables(false);
            return;
          }
        }

        // 2. Fallback: chiama direttamente la RPC dal client
        const { data: rpcData, error: rpcError } = await supabase
          .rpc('get_tables_by_pattern', { search_pattern: 'cp%ev_%' });
        
        if (!rpcError && rpcData) {
          // La RPC restituisce un array di stringhe o oggetti con table_name
          const formatted = rpcData.map((t: any) => {
            const name = typeof t === 'string' ? t : t.table_name;
            return { 
              table_name: name, 
              table_type: name.endsWith('_view') ? 'VIEW' : 'BASE TABLE'
            };
          });
          setTables(formatted);
          setLoadingTables(false);
          return;
        }

        // 3. Fallback estremo: information_schema (se l'utente ha permessi diretti)
        const { data, error } = await supabase
          .schema('information_schema')
          .from('tables')
          .select('table_name, table_type')
          .eq('table_schema', 'public');
        
        if (error) throw error;
        setTables(data || []);
      } catch (err) {
        console.warn('SysEdit: Error fetching tables, using hardcoded fallback', err);
        setTables([
          { table_name: 'cpgev_anagrafica', table_type: 'BASE TABLE' },
          { table_name: 'cpgev_config', table_type: 'BASE TABLE' },
          { table_name: 'cpgev_soci', table_type: 'BASE TABLE' },
          { table_name: 'cpgev_gruppi', table_type: 'BASE TABLE' },
          { table_name: 'cpgev_verbali', table_type: 'BASE TABLE' }
        ]);
      } finally {
        setLoadingTables(false);
      }
    };
    fetchTables();
  }, []);

  return (
    <div className="container mx-auto pb-8 pt-0">
      <input 
        type="file" 
        ref={fileInputRef} 
        onChange={handleFileImport} 
        className="hidden" 
        accept={currentAction?.action === 'json' ? '.json' : currentAction?.action === 'csv' ? '.csv' : '.sql,.txt'}
      />
      
      {loadingTables ? (
        <div className="p-12 bg-white rounded-2xl border border-slate-200 text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-500 font-medium">Recupero lista tabelle...</p>
        </div>
      ) : (
        <DBTable 
          title="Gestione Tabelle Sistema"
          schema="information_schema"
          tableName="tables"
          columns={['table_name', 'table_type']}
          whereClause="table_schema = 'public'"
          columnLabels={{ table_name: 'Nome Oggetto', table_type: 'Tipo' }}
          pinnedColumns={1}
          pageSize={1000}
          sortable={true}
          searchable={true}
          filterable={true}
          selectable={true}
          onAction={handleAction}
          onRowDoubleClick={handleRowDoubleClick}
          initialData={tables}
        />
      )}
    </div>
  );
};

export default SysEdit;
